import { t } from "@travily/i18n";
import type ProductsIndex from "@travily/types/pages/ProductsIndex";
import type MarketplaceSearchHit from "@travily/types/pending/models/MarketplaceSearchHit";
import { SearchBar } from "@ui/components/SearchBar";
import { SortMenu } from "@ui/components/SortMenu";
import { usdPriceScale } from "@ui/lib/price-display";
import { useMenu, useRange, useSearchBox } from "react-instantsearch";
import { ListingCardAdapter } from "@/components/ListingCard/ListingCardAdapter";
import { ListingCardSkeleton } from "@/components/ListingCard/ListingCardSkeleton";
import { MarketplaceFilters } from "@/components/MarketplaceSearch/components/MarketplaceFilters";
import type { MarketplaceTypeFilterValue } from "@/components/MarketplaceSearch/components/MarketplaceTypeFilter";
import { MarketplaceHitCard } from "@/components/MarketplaceSearch/MarketplaceHitCard";
import { MarketplaceSearchField } from "@/components/MarketplaceSearch/MarketplaceSearchField";
import { SearchResultsBody } from "@/components/Search/SearchResultsBody";
import { SearchResultsHeading } from "@/components/Search/SearchResultsHeading";
import { useSearchResults } from "@/hooks/use-search-results";
import { useMarketplaceSort } from "./MarketplaceSortContext";

function useMarketplaceFilters() {
  const typeMenu = useMenu({ attribute: "type" });
  const priceRangeHook = useRange({ attribute: "price_cents" });

  const refinedType = typeMenu.items.find((item) => item.isRefined);
  const typeValue: MarketplaceTypeFilterValue = (refinedType?.value as MarketplaceTypeFilterValue) ?? "all";

  const scale = usdPriceScale();

  const priceMinCents = priceRangeHook.range.min ?? 0;
  const priceMaxCents = priceRangeHook.range.max ?? 0;
  const startMinCents = Number.isFinite(priceRangeHook.start[0]) ? (priceRangeHook.start[0] as number) : priceMinCents;
  const startMaxCents = Number.isFinite(priceRangeHook.start[1]) ? (priceRangeHook.start[1] as number) : priceMaxCents;

  const priceMin = scale.centsToDisplayFloor(priceMinCents);
  const priceMax = scale.centsToDisplayCeil(priceMaxCents);
  const priceValue: [number, number] = [
    Math.max(priceMin, scale.centsToDisplayFloor(startMinCents)),
    Math.min(priceMax, scale.centsToDisplayCeil(startMaxCents)),
  ];

  return {
    currencySymbol: scale.symbol,
    handlePriceChange: (range: [number, number]) => {
      const minCents = scale.displayToCentsMin(range[0]);
      const maxCents = Math.max(minCents, scale.displayToCentsMax(range[1]));
      priceRangeHook.refine([minCents, maxCents]);
    },
    handleTypeChange: (value: MarketplaceTypeFilterValue) => {
      if (value === "all") {
        if (refinedType) typeMenu.refine(refinedType.value);
      } else {
        typeMenu.refine(value);
      }
    },
    priceRange: { min: priceMin, max: priceMax },
    priceValue,
    typeValue,
  };
}

interface MarketplaceSearchAdapterProps {
  getFavorited?: (type: string, id: string | number) => boolean;
  onFavoriteToggle?: (type: string, id: string | number) => void;
  /** Server-computed default first page (see Web::Concerns::SeedResults). */
  seedListings?: ProductsIndex["seed_results"];
  /** The default search's total, seeded so the H1 count is server-rendered. */
  seedTotal?: ProductsIndex["seed_total"];
}

export function MarketplaceSearchAdapter({
  getFavorited,
  onFavoriteToggle,
  seedListings,
  seedTotal,
}: MarketplaceSearchAdapterProps) {
  const results = useSearchResults<MarketplaceSearchHit>({ sessionKey: "marketplace" });
  const sort = useMarketplaceSort();

  const showLive = !(results.isInitialLoading && results.hits.length === 0);
  const count = showLive ? results.totalCount : (seedTotal ?? null);
  const heading = count === null ? null : t("components.views.shared.marketplace_search.heading", { count });
  const seedCards = seedListings?.map((listing) => (
    <ListingCardAdapter
      favorited={getFavorited?.("Listing", listing.sqid) ?? false}
      key={listing.sqid}
      listing={listing}
      onFavoriteToggle={onFavoriteToggle ? () => onFavoriteToggle("Listing", listing.sqid) : undefined}
      showAuthor
      theme="compact"
    />
  ));

  return (
    <div className="flex w-full flex-col gap-6">
      <SearchResultsHeading
        actions={
          /* Sort + filters live in the SearchBar (md+, see index.tsx). Below md the
           *  search-centric top bar carries filters, so the heading keeps only sort. */
          <div className="md:hidden">
            <SortMenu currentValue={sort.currentValue} onChange={sort.refine} options={sort.options} size="36" />
          </div>
        }
        heading={heading}
      />
      <SearchResultsBody
        emptyDescription={t("components.views.shared.marketplace_search.empty_description")}
        emptyTitle={t("components.views.shared.marketplace_search.empty_title")}
        getKey={(hit) => `${hit.type}:${hit.sqid}`}
        illustration="products"
        renderCard={(hit) => (
          <MarketplaceHitCard
            favorited={getFavorited?.("Listing", hit.sqid) ?? false}
            hit={hit}
            onFavoriteToggle={onFavoriteToggle ? () => onFavoriteToggle("Listing", hit.sqid) : undefined}
          />
        )}
        renderSkeletonCard={(key) => <ListingCardSkeleton key={key} />}
        results={results}
        seedCards={seedCards}
        skeletonCount={15}
      />
    </div>
  );
}

/** The desktop (md+) search row: the type-to-search field plus the Sort and Filters pills
 *  share one `SearchBar` shell so they read as a single bar, matching /destinations. */
export function MarketplaceSearchBarAdapter() {
  const { query, refine } = useSearchBox();
  const sort = useMarketplaceSort();

  return (
    <SearchBar>
      <MarketplaceSearchField onChange={refine} value={query} />
      <SearchBar.Sort currentValue={sort.currentValue} onChange={sort.refine} options={sort.options} />
      <SearchBar.Filters>
        <MarketplaceFiltersAdapter />
      </SearchBar.Filters>
    </SearchBar>
  );
}

export function MarketplaceFiltersAdapter() {
  const filters = useMarketplaceFilters();

  return (
    <MarketplaceFilters
      currencySymbol={filters.currencySymbol}
      onPriceChange={filters.handlePriceChange}
      onTypeChange={filters.handleTypeChange}
      priceRange={filters.priceRange}
      priceValue={filters.priceValue}
      typeValue={filters.typeValue}
    />
  );
}
